import { tracking } from '@iwoca/frontend-tracking-library';
import { IconDownload } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { t } from '@lingui/macro';
import { useMutation } from '@tanstack/react-query';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { fetchGetEarlyRepaymentSchedulePDF } from 'lendingApi/lendingApi';
import { useGetIntroducerApplication } from 'lendingApi/lendingApiHooks';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { TDrawdownRepaymentQueryParams } from 'utils/tests/urls.config';

type TProps = {
  offerId: string;
  pdfQuery: TDrawdownRepaymentQueryParams;
  displayError: (hasError: boolean) => void;
};

export const ExportRepaymentScheduleToPDFButton = ({
  pdfQuery,
  offerId,
  displayError,
}: TProps) => {
  const { application } = useGetIntroducerApplication();
  const { introducerId } = useIntroducerContext();
  const applicationId = application?.application_uid as string;
  const stateKey = application?.customer?.state_key;

  const { mutate: mutateRepaymentPlanPDF, isPending: isLoadingPDFDownload } =
    useMutation({
      mutationFn: async () => {
        tracking.ampli.repaymentSchedulePdfExport({
          ...getAmplitudeDefaultEventProperties(),
        });
        displayError(false);

        if (!stateKey || !applicationId) {
          throw new Error('Missing stateKey or applicationId');
        }

        const response = await fetchGetEarlyRepaymentSchedulePDF({
          introducerId: introducerId!,
          applicationId,
          offerId,
          query: pdfQuery,
        });

        if (!response.ok) {
          throw new Error('Failed to download PDF');
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        const fileName =
          contentDisposition && /filename="(.+)"/.exec(contentDisposition)?.[1]
            ? /filename="(.+)"/.exec(contentDisposition)![1]
            : `${t`repayment-schedule`}_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.pdf`;

        const blob = await response.blob();

        return { blob, fileName };
      },
      onSuccess: ({ blob, fileName }) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        saveAs(pdfBlob, fileName);
      },
      onError: () => {
        displayError(true);
      },
    });

  if (!stateKey || !applicationId) {
    return null;
  }

  return (
    <Button
      variant="tertiary"
      data-testid="export-repayment-schedule-pdf-button"
      size="small"
      onClick={() => mutateRepaymentPlanPDF()}
      loading={isLoadingPDFDownload}
    >
      <IconDownload />
      PDF
    </Button>
  );
};
